<template>
  <div class="tenant-sidebar-shell">
    <el-scrollbar
      class="tenant-sidebar-scroll"
      wrap-class="tenant-sidebar-scroll-wrap"
      view-class="tenant-sidebar-scroll-view"
    >
      <slot />
    </el-scrollbar>
    <div
      v-show="!collapsed"
      class="tenant-sidebar-resizer"
      :class="{ 'is-resizing': resizing }"
      role="separator"
      aria-label="调整侧边栏宽度"
      aria-orientation="vertical"
      :aria-valuemin="minWidth"
      :aria-valuemax="maxWidth"
      :aria-valuenow="sidebarWidth"
      tabindex="0"
      @pointerdown="startResize"
      @keydown="handleResizeKeydown"
      @dblclick="resetWidth"
    />
    <button
      type="button"
      class="tenant-sidebar-footer"
      :class="{ 'is-collapse': collapsed }"
      aria-label="切换侧边栏"
      @click="$emit('toggle')"
    >
      <svg class="tenant-sidebar-toggle" viewBox="0 0 1024 1024" aria-hidden="true">
        <path
          fill="currentColor"
          d="M408 442h480c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8H408c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm0 212h480c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8H408c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm496-494H120c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zM142.4 642.1 298.7 519a8.84 8.84 0 0 0 0-13.9L142.4 381.9c-5.8-4.6-14.4-.5-14.4 6.9v246.3a8.9 8.9 0 0 0 14.4 7z"
        />
      </svg>
    </button>
  </div>
</template>

<script>
import {
  TENANT_SIDEBAR_DEFAULT_WIDTH,
  TENANT_SIDEBAR_MAX_WIDTH,
  TENANT_SIDEBAR_MIN_WIDTH,
  normalizeTenantSidebarWidth,
  readTenantSidebarWidth,
  saveTenantSidebarWidth
} from '@/utils/tenant-sidebar'

export default {
  name: 'TenantSidebarShell',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxWidth: TENANT_SIDEBAR_MAX_WIDTH,
      minWidth: TENANT_SIDEBAR_MIN_WIDTH,
      resizeStartWidth: TENANT_SIDEBAR_DEFAULT_WIDTH,
      resizeStartX: 0,
      resizing: false,
      sidebarWidth: TENANT_SIDEBAR_DEFAULT_WIDTH
    }
  },
  watch: {
    collapsed(value) {
      if (value) {
        this.stopResize()
      }
    }
  },
  mounted() {
    this.applyWidth(readTenantSidebarWidth(this.getStorage()))
  },
  beforeDestroy() {
    this.stopResize()
  },
  methods: {
    getStorage() {
      try {
        return typeof window === 'undefined' ? null : window.localStorage
      } catch (error) {
        return null
      }
    },
    applyWidth(value, persist = false) {
      const width = persist
        ? saveTenantSidebarWidth(this.getStorage(), value)
        : normalizeTenantSidebarWidth(value)
      this.sidebarWidth = width
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty(
          '--tenant-sidebar-width',
          `${width}px`
        )
      }
      return width
    },
    startResize(event) {
      if (this.collapsed || (event.button !== undefined && event.button !== 0)) {
        return
      }
      event.preventDefault()
      this.resizing = true
      this.resizeStartX = event.clientX
      this.resizeStartWidth = this.sidebarWidth
      if (typeof document !== 'undefined') {
        document.body.classList.add('tenant-sidebar-is-resizing')
        document.addEventListener('pointermove', this.handleResize)
        document.addEventListener('pointerup', this.stopResize)
        document.addEventListener('pointercancel', this.stopResize)
      }
    },
    handleResize(event) {
      if (!this.resizing) {
        return
      }
      this.applyWidth(
        this.resizeStartWidth + event.clientX - this.resizeStartX
      )
    },
    stopResize() {
      const shouldPersist = this.resizing
      this.resizing = false
      if (typeof document !== 'undefined') {
        document.body.classList.remove('tenant-sidebar-is-resizing')
        document.removeEventListener('pointermove', this.handleResize)
        document.removeEventListener('pointerup', this.stopResize)
        document.removeEventListener('pointercancel', this.stopResize)
      }
      if (shouldPersist) {
        this.applyWidth(this.sidebarWidth, true)
      }
    },
    resizeBy(offset) {
      this.applyWidth(this.sidebarWidth + offset, true)
    },
    handleResizeKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.resizeBy(-10)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.resizeBy(10)
      } else if (event.key === 'Home') {
        event.preventDefault()
        this.resetWidth()
      }
    },
    resetWidth() {
      this.applyWidth(TENANT_SIDEBAR_DEFAULT_WIDTH, true)
    }
  }
}
</script>
